import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOptionalWebSocket } from '../../../contexts/WebSocketContext';
import './ChatPanel.css';

const createClientMessageId = () => {
  const value = typeof window !== 'undefined' && window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  return `chat:${value}`;
};

const displayTime = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const ChatPanel = () => {
  const socketContext = useOptionalWebSocket();
  const {
    currentSessionId,
    isGameMaster,
    sessionParticipants,
    isConnected,
    addEventListener,
    webSocketService,
  } = socketContext || {};
  const [messages, setMessages] = useState([]);
  const [olderCursor, setOlderCursor] = useState(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [unread, setUnread] = useState(0);
  const [text, setText] = useState('');
  const [audience, setAudience] = useState('session');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [retryDraft, setRetryDraft] = useState(null);
  const endRef = useRef(null);
  const panelRef = useRef(null);
  const panelVisibleRef = useRef(true);
  const activeSessionRef = useRef(null);
  const cursorRef = useRef(null);

  const mergeMessages = useCallback((incoming) => {
    setMessages((current) => {
      const byId = new Map(current.map((message) => [message.messageId, message]));
      (Array.isArray(incoming) ? incoming : [incoming]).filter(Boolean).forEach((message) => byId.set(message.messageId, message));
      return Array.from(byId.values()).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    });
  }, []);

  const loadHistory = useCallback(async (requestedCursor = null, direction = 'after') => {
    if (!currentSessionId || !isConnected || !webSocketService?.emitRequest) return;
    setStatus('loading');
    setError('');
    try {
      const isReconnectCatchup = direction === 'after' && requestedCursor !== null;
      let nextCursor = requestedCursor;
      let response;
      let firstResponse;
      let pageCount = 0;
      do {
        response = await webSocketService.emitRequest('chat:history', { sessionId: currentSessionId, cursor: nextCursor, direction });
        firstResponse ||= response;
        mergeMessages(response.messages || []);
        if (!isReconnectCatchup) break;
        nextCursor = response.cursor || nextCursor || null;
        pageCount += 1;
      } while (response.hasMore && pageCount < 20);

      if (direction !== 'before') {
        const latestCursor = response?.cursor || requestedCursor || null;
        cursorRef.current = latestCursor;
      }
      setOlderCursor(response?.olderCursor || requestedCursor || null);
      if (direction === 'before' || requestedCursor === null) setHasOlder(Boolean(firstResponse?.hasMore));
      setStatus('idle');
    } catch (cause) {
      setStatus('error');
      setError(cause?.message || 'Chat history could not be loaded.');
    }
  }, [currentSessionId, isConnected, mergeMessages, webSocketService]);

  useEffect(() => {
    if (activeSessionRef.current === currentSessionId) return;
    activeSessionRef.current = currentSessionId || null;
    cursorRef.current = null;
    setMessages([]);
    setOlderCursor(null);
    setHasOlder(false);
    setUnread(0);
  }, [currentSessionId]);

  useEffect(() => {
    if (currentSessionId && isConnected) loadHistory(cursorRef.current, 'after');
  }, [currentSessionId, isConnected, loadHistory]);

  useEffect(() => {
    if (typeof addEventListener !== 'function') return undefined;
    return addEventListener('chat:message', (message) => {
      mergeMessages(message);
      cursorRef.current = message?.messageId || cursorRef.current;
      if (document.hidden || !panelVisibleRef.current) setUnread((count) => count + 1);
    });
  }, [addEventListener, mergeMessages]);

  useEffect(() => {
    const node = panelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      panelVisibleRef.current = Boolean(entry?.isIntersecting);
      if (entry?.isIntersecting && !document.hidden) setUnread(0);
    }, { threshold: 0.25 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const visible = () => { if (!document.hidden && panelVisibleRef.current) setUnread(0); };
    document.addEventListener('visibilitychange', visible);
    return () => document.removeEventListener('visibilitychange', visible);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [messages]);

  const recipients = useMemo(() => {
    const table = [{ value: 'session', label: 'Everyone at the table' }];
    if (isGameMaster) {
      const unique = new Map((sessionParticipants || []).filter((participant) => participant.role !== 'gm').map((participant) => [participant.userId, participant]));
      unique.forEach((participant) => table.push({ value: `user:${participant.userId}`, label: `Private: ${participant.userName || 'Player'}` }));
    } else {
      table.push({ value: 'gm', label: 'Private: Game Master' });
    }
    return table;
  }, [isGameMaster, sessionParticipants]);

  const send = useCallback(async (draft) => {
    if (!currentSessionId || !isConnected || !webSocketService?.emitRequest) {
      setError('Chat is offline. Your message has not been sent.');
      setRetryDraft(draft);
      return;
    }
    setStatus('sending');
    setError('');
    try {
      const response = await webSocketService.emitRequest('chat:send', { sessionId: currentSessionId, ...draft });
      mergeMessages(response.message);
      cursorRef.current = response.message?.messageId || cursorRef.current;
      setRetryDraft(null);
      setStatus('idle');
    } catch (cause) {
      setStatus('error');
      setError(cause?.message || 'Message was not sent.');
      setRetryDraft(draft);
    }
  }, [currentSessionId, isConnected, mergeMessages, webSocketService]);

  const submit = (event) => {
    event.preventDefault();
    const normalized = text.trim();
    if (!normalized || status === 'sending') return;
    const draft = { clientMessageId: createClientMessageId(), audience, text: normalized };
    setText('');
    send(draft);
  };

  const remove = async (messageId) => {
    if (!webSocketService?.emitRequest) return;
    if (!window.confirm('Remove this message for everyone who can see it?')) return;
    setError('');
    try {
      const response = await webSocketService.emitRequest('chat:remove', { sessionId: currentSessionId, messageId });
      mergeMessages(response.message);
    } catch (cause) {
      setError(cause?.message || 'Message could not be removed.');
    }
  };

  return (
    <section className="br-chat" aria-label="BattleRoom chat" ref={panelRef}>
      <div className="br-chat__toolbar">
        <label htmlFor="br-chat-audience">Send to</label>
        <select id="br-chat-audience" value={audience} onChange={(event) => setAudience(event.target.value)} disabled={!isConnected}>
          {recipients.map((recipient) => <option key={recipient.value} value={recipient.value}>{recipient.label}</option>)}
        </select>
        <span className={`br-chat__connection ${isConnected ? 'is-online' : 'is-offline'}`}>{isConnected ? 'Live' : 'Offline'}</span>
        {unread > 0 && <button type="button" className="br-chat__unread" onClick={() => { setUnread(0); endRef.current?.scrollIntoView?.({ block: 'nearest' }); }}>{unread} unread</button>}
      </div>

      <div className="br-chat__messages" role="log" aria-live="polite" aria-relevant="additions text">
        {hasOlder && <button type="button" className="br-chat__older" disabled={status === 'loading'} onClick={() => loadHistory(olderCursor, 'before')}>Load earlier messages</button>}
        {messages.length === 0 && status !== 'loading' ? (
          <p className="br-chat__empty">No messages yet. Table chat is saved and returns after reconnect.</p>
        ) : messages.map((message) => {
          const recipient = message.audience?.startsWith('user:')
            ? (sessionParticipants || []).find((participant) => String(participant.userId) === String(message.audience.slice(5)))
            : null;
          const privateLabel = message.audience === 'gm'
            ? 'Private to Game Master'
            : message.audience?.startsWith('user:') ? `Private to ${recipient?.userName || 'participant'}` : null;
          return (
            <article className={`br-chat__message ${privateLabel ? 'is-private' : ''}`} key={message.messageId}>
              <header>
                <strong>{message.senderName || 'Participant'}</strong>
                <time dateTime={message.createdAt}>{displayTime(message.createdAt)}</time>
                {privateLabel && <span className="br-chat__private-label">{privateLabel}</span>}
              </header>
              {message.type === 'removed' ? <p className="br-chat__removed">Message removed by the Game Master.</p> : <p>{message.text}</p>}
              {isGameMaster && message.type !== 'removed' && (
                <button className="br-chat__remove" type="button" onClick={() => remove(message.messageId)} aria-label={`Remove message from ${message.senderName || 'participant'}`}>Remove</button>
              )}
            </article>
          );
        })}
        <div ref={endRef} />
      </div>

      {error && <div className="br-chat__error" role="alert">{error}{retryDraft && <button type="button" onClick={() => send(retryDraft)}>Retry</button>}</div>}
      <form className="br-chat__composer" onSubmit={submit}>
        <label className="br-chat__sr-only" htmlFor="br-chat-message">Message</label>
        <textarea id="br-chat-message" value={text} onChange={(event) => setText(event.target.value)} maxLength={1000} rows={2} placeholder={isConnected ? 'Write a table message…' : 'Reconnect to send chat'} disabled={!currentSessionId} />
        <div className="br-chat__composer-footer">
          <span>{text.length}/1000</span>
          <button type="submit" disabled={!text.trim() || !isConnected || status === 'sending'}>{status === 'sending' ? 'Sending…' : 'Send'}</button>
        </div>
      </form>
    </section>
  );
};

export default ChatPanel;
